import {
  Button,
  FileInput,
  Group,
  Image,
  LoadingOverlay,
  MultiSelect,
  NumberInput,
  Paper,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useQuery } from "@tanstack/react-query";
import { Video } from "lucide-react";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getLanguage } from "../../utils/api";
import {
  useAddVerticalSeries,
  useUpdateVerticalSeries,
} from "../../utils/mutations";
import { useGenres, useVerticalSeriesById } from "../../utils/queries";
import useStore from "../../utils/store";
import BunnyVideoSelector from "../../components/common/BunnyVideoSelector";
import { videoAPI } from "../../services/bunnyApi";

const AGE_RATINGS = [
  { label: "13+", value: "13+" },
  { label: "16+", value: "16+" },
  { label: "18+", value: "18+" },
  { label: "U", value: "U" },
  { label: "U/A", value: "U/A" },
  { label: "A", value: "A" },
];

const defaultFormValues = () => ({
  name: "",
  description: "",
  trailer: "",
  genres: [],
  ageLimit: "",
  language: "",
  releaseDate: null,
  bannerWithTitleImage: null,
  titleImage: null,
  bannerImage: null,
  stripEnable: false,
  stripText: "",
  status: "Pending",
  isEnable: false,
  isBanner: false,
  bannerIndex: 1,
  isPopular: false,
  popularIndex: 1,
  isUpdatedRecently: false,
  updatedRecentlyIndex: 1,
  isMoreShows: false,
  moreShowIndex: 1,
  isHotShow: false,
  hotShowIndex: 1,
  isNewShow: false,
  newShowIndex: 1,
  isOriginalShow: false,
  originalShowIndex: 1,
});

const AddVerticalSeries = () => {
  const user = useStore((state) => state.user);
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const id = params.id;
  const page = searchParams.get("page") || "1";

  const { data: genres } = useGenres();
  const { data: languages } = useQuery({
    queryKey: ["languages"],
    queryFn: getLanguage,
  });

  const { isPending, mutate } = useAddVerticalSeries();
  const { isPending: isUpdating, mutate: update } = useUpdateVerticalSeries({
    onSuccess: () => {
      navigate(`/vertical-series?page=${page}`);
    },
  });
  const { data: updateData, isLoading } = useVerticalSeriesById(params.id);
  const [bunnyTrailerOpen, setBunnyTrailerOpen] = useState(false);

  const form = useForm({
    initialValues: defaultFormValues(),
    validate: {
      name: (v) => (!v || !String(v).trim() ? "Please enter a name" : null),
      description: (v) =>
        !v || !String(v).trim() ? "Please enter a description" : null,
      genres: (v) =>
        !v || v.length === 0 ? "Please select at least one genre" : null,
      bannerWithTitleImage: (v) =>
        !id && !v ? "Please upload a banner with title image" : null,
      titleImage: (v) => (!id && !v ? "Please upload a title image" : null),
      bannerImage: (v) => (!id && !v ? "Please upload a banner image" : null),
      stripText: (v, values) =>
        values.stripEnable && (!v || !String(v).trim())
          ? "Strip text is required when strip is enabled"
          : null,
    },
  });

  useEffect(() => {
    if (id && user?.updateVerticalSeries === false) {
      notifications.show({
        message: "You don't have permission to edit Short Drama",
        color: "red",
      });
      navigate(`/vertical-series?page=${page}`);
    } else if (!id && user?.addVerticalSeries === false) {
      notifications.show({
        message: "You don't have permission to add Short Drama",
        color: "red",
      });
      navigate(`/vertical-series?page=${page}`);
    }
  }, [id, user, navigate, page]);

  useEffect(() => {
    if (updateData) {
      form.setValues({
        name: updateData?.name ?? "",
        description: updateData?.description ?? "",
        genres: updateData?.genres?.map((item) => item._id) ?? [],
        trailer: updateData?.trailer ?? "",
        ageLimit: updateData?.ageLimit ?? "",
        language: updateData?.language ?? "",
        releaseDate: updateData?.releaseDate
          ? new Date(updateData.releaseDate)
          : null,
        bannerWithTitleImage: null,
        titleImage: null,
        bannerImage: null,
        stripEnable: updateData?.stripEnable || false,
        stripText: updateData?.stripText || "",
        status: updateData?.status || "Pending",
        isBanner: updateData?.isBanner || false,
        bannerIndex: updateData?.bannerIndex ?? 1,
        isPopular: updateData?.isPopular || false,
        popularIndex: updateData?.popularIndex ?? 1,
        isUpdatedRecently: updateData?.isUpdatedRecently || false,
        updatedRecentlyIndex: updateData?.updatedRecentlyIndex ?? 1,
        isMoreShows: updateData?.isMoreShows || false,
        moreShowIndex: updateData?.moreShowIndex ?? 1,
        isHotShow: updateData?.isHotShow || false,
        hotShowIndex: updateData?.hotShowIndex ?? 1,
        isNewShow: updateData?.isNewShow || false,
        newShowIndex: updateData?.newShowIndex ?? 1,
        isOriginalShow: updateData?.isOriginalShow || false,
        originalShowIndex: updateData?.originalShowIndex ?? 1,
        isEnable: updateData?.isEnable || false,
      });
    } else if (!id) {
      form.setValues(defaultFormValues());
    }
  }, [updateData, id, form]);

  const handleFormSubmit = (values) => {
    const formData = new FormData();

    if (values.bannerWithTitleImage) {
      formData.append("bannerWithTitleImage", values.bannerWithTitleImage);
    }
    if (values.titleImage) {
      formData.append("titleImage", values.titleImage);
    }
    if (values.bannerImage) {
      formData.append("bannerImage", values.bannerImage);
    }

    formData.append("name", values.name);
    formData.append("description", values.description);
    formData.append("genres", JSON.stringify(values.genres));

    if (values.trailer) {
      formData.append("trailer", values.trailer);
    }
    if (values.language) {
      formData.append("language", values.language);
    }
    if (values.ageLimit) {
      formData.append("ageLimit", values.ageLimit);
    }
    if (values.releaseDate) {
      formData.append("releaseDate", dayjs(values.releaseDate).toISOString());
    }

    formData.append("stripEnable", values.stripEnable || false);
    if (values.stripText) {
      formData.append("stripText", values.stripText);
    }

    formData.append("status", values.status || "Pending");

    formData.append("isBanner", values.isBanner || false);
    formData.append("bannerIndex", values.bannerIndex || 1);
    formData.append("isPopular", values.isPopular || false);
    formData.append("popularIndex", values.popularIndex || 1);
    formData.append("isUpdatedRecently", values.isUpdatedRecently || false);
    formData.append("updatedRecentlyIndex", values.updatedRecentlyIndex || 1);
    formData.append("isMoreShows", values.isMoreShows || false);
    formData.append("moreShowIndex", values.moreShowIndex || 1);
    formData.append("isHotShow", values.isHotShow || false);
    formData.append("hotShowIndex", values.hotShowIndex || 1);
    formData.append("isNewShow", values.isNewShow || false);
    formData.append("newShowIndex", values.newShowIndex || 1);
    formData.append("isOriginalShow", values.isOriginalShow || false);
    formData.append("originalShowIndex", values.originalShowIndex || 1);

    if (user?.isPublishAccess) {
      formData.append("isEnable", values.isEnable || false);
    }

    if (id) {
      update({ id: id, data: formData });
    } else {
      mutate(formData);
    }
  };

  if (
    user?.addVerticalSeries === false &&
    user?.updateVerticalSeries === false
  ) {
    return null;
  }

  const languageOptions = (languages || []).map((item) => ({
    value: item.name,
    label: item.name,
  }));

  const genreOptions = (genres || []).map((item) => ({
    value: item._id,
    label: item.name,
  }));

  return (
    <>
      <Paper
        withBorder
        p="md"
        pos="relative"
        style={{
          maxWidth: "600px",
          width: "100%",
          marginInline: "auto",
        }}
      >
        <LoadingOverlay visible={isLoading} />
        <Title order={3} mb="md">
          {id ? "Edit Short Drama" : "Add Short Drama"}
        </Title>
        <form
          onSubmit={form.onSubmit(handleFormSubmit, () => {
            notifications.show({
              message: "Please fill all required fields",
              color: "red",
            });
          })}
        >
          <Stack gap="md">
            <TextInput
              label="Name"
              placeholder="Enter name"
              withAsterisk
              {...form.getInputProps("name")}
            />

            <Textarea
              label="Description"
              placeholder="Enter description"
              rows={4}
              withAsterisk
              {...form.getInputProps("description")}
            />

            <div>
              <Text size="sm" fw={500} mb={4}>
                Trailer (Bunny)
              </Text>
              <Group gap="sm" align="flex-end" wrap="nowrap">
                <TextInput
                  style={{ flex: 1 }}
                  placeholder="Select from Bunny library or paste URL"
                  {...form.getInputProps("trailer")}
                />
                <Button
                  type="button"
                  variant="default"
                  leftSection={<Video size={16} />}
                  onClick={() => setBunnyTrailerOpen(true)}
                >
                  Select trailer
                </Button>
              </Group>
            </div>

            <DatePickerInput
              label="Release Date"
              placeholder="Pick date"
              clearable
              value={form.values.releaseDate}
              onChange={(d) => form.setFieldValue("releaseDate", d)}
            />

            <Select
              label="Language"
              placeholder="Select language"
              searchable
              clearable
              data={languageOptions}
              {...form.getInputProps("language")}
            />

            <Select
              label="Age Limit"
              placeholder="Select age limit"
              clearable
              data={AGE_RATINGS}
              {...form.getInputProps("ageLimit")}
            />

            <MultiSelect
              label="Genres"
              placeholder="Select genres"
              searchable
              withAsterisk
              data={genreOptions}
              {...form.getInputProps("genres")}
            />

            <div>
              {updateData?.bannerWithTitleImage && (
                <div style={{ marginBottom: "16px" }}>
                  <Image
                    src={updateData.bannerWithTitleImage}
                    alt="Banner With Title Preview"
                    style={{ maxWidth: "100%", maxHeight: "200px" }}
                  />
                </div>
              )}
              <FileInput
                label="Banner With Title Image (1280 x 720 px)"
                placeholder="Click to upload"
                accept="image/*"
                clearable
                withAsterisk={!id}
                {...form.getInputProps("bannerWithTitleImage")}
              />
            </div>

            <div>
              {updateData?.titleImage && (
                <div style={{ marginBottom: "16px" }}>
                  <Image
                    src={updateData.titleImage}
                    alt="Title Image Preview"
                    style={{ maxWidth: "100%", maxHeight: "200px" }}
                  />
                </div>
              )}
              <FileInput
                label="Title Image (900 x 1200 px)"
                placeholder="Click to upload"
                accept="image/*"
                clearable
                withAsterisk={!id}
                {...form.getInputProps("titleImage")}
              />
            </div>

            <div>
              {updateData?.bannerImage && (
                <div style={{ marginBottom: "16px" }}>
                  <Image
                    src={updateData.bannerImage}
                    alt="Banner Image Preview"
                    style={{ maxWidth: "100%", maxHeight: "200px" }}
                  />
                </div>
              )}
              <FileInput
                label="Banner Image (1280 x 720 px)"
                placeholder="Click to upload"
                accept="image/*"
                clearable
                withAsterisk={!id}
                {...form.getInputProps("bannerImage")}
              />
            </div>

            <Switch
              label="Strip Enable"
              {...form.getInputProps("stripEnable", { type: "checkbox" })}
            />

            <Textarea
              label="Strip Text"
              placeholder="Enter strip text"
              rows={3}
              disabled={!form.values.stripEnable}
              {...form.getInputProps("stripText")}
            />

            {user?.isPublishAccess && (
              <>
                <Select
                  label="Status"
                  data={[
                    { value: "Draft", label: "Draft" },
                    { value: "Pending", label: "Pending" },
                    { value: "Approved", label: "Approved" },
                    { value: "Rejected", label: "Rejected" },
                  ]}
                  {...form.getInputProps("status")}
                />
                <Switch
                  label="Enable/Disable"
                  {...form.getInputProps("isEnable", { type: "checkbox" })}
                />
              </>
            )}

            <Switch
              label="Is Banner"
              {...form.getInputProps("isBanner", { type: "checkbox" })}
            />

            <NumberInput
              label="Banner Index"
              min={1}
              {...form.getInputProps("bannerIndex")}
            />

            <Switch
              label="Is Popular"
              {...form.getInputProps("isPopular", { type: "checkbox" })}
            />

            <NumberInput
              label="Popular Index"
              min={1}
              {...form.getInputProps("popularIndex")}
            />

            <Switch
              label="Is Updated Recently"
              {...form.getInputProps("isUpdatedRecently", { type: "checkbox" })}
            />

            <NumberInput
              label="Updated Recently Index"
              min={1}
              {...form.getInputProps("updatedRecentlyIndex")}
            />

            <Switch
              label="Is More Shows"
              {...form.getInputProps("isMoreShows", { type: "checkbox" })}
            />

            <NumberInput
              label="More Show Index"
              min={1}
              {...form.getInputProps("moreShowIndex")}
            />

            <Switch
              label="Is Hot Show"
              {...form.getInputProps("isHotShow", { type: "checkbox" })}
            />

            <NumberInput
              label="Hot Show Index"
              min={1}
              {...form.getInputProps("hotShowIndex")}
            />

            <Switch
              label="Is New Show"
              {...form.getInputProps("isNewShow", { type: "checkbox" })}
            />

            <NumberInput
              label="New Show Index"
              min={1}
              {...form.getInputProps("newShowIndex")}
            />

            <Switch
              label="Is Original Show"
              {...form.getInputProps("isOriginalShow", { type: "checkbox" })}
            />

            <NumberInput
              label="Original Show Index"
              min={1}
              {...form.getInputProps("originalShowIndex")}
            />

            <Button type="submit" loading={isPending || isUpdating}>
              {id ? "Update" : "Add"}
            </Button>
          </Stack>
        </form>
      </Paper>

      <BunnyVideoSelector
        open={bunnyTrailerOpen}
        onCancel={() => setBunnyTrailerOpen(false)}
        onSelect={(video) => {
          form.setFieldValue("trailer", videoAPI.getPlaylistUrl(video.guid));
          notifications.show({
            message: `Trailer "${video.title}" selected`,
            color: "green",
          });
        }}
        title="Select trailer (Bunny)"
        verticalOnly
      />
    </>
  );
};

export default AddVerticalSeries;
